import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';
import { AnalyzerConfig, Finding, AnalyzerResult } from '../types/contractAnalyzer';

const API_BASE_URL = `${ENV.API_URL}/api`;

async function getToken(): Promise<string> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('Error getting Supabase auth token:', e);
    return '';
  }
}

export async function analyzeWithLLM(text: string, config: AnalyzerConfig): Promise<AnalyzerResult> {
  if (!text || !text.trim()) {
    throw new Error('Contract text is required for analysis');
  }
  if (!config || !config.primaryJurisdiction || !config.contractType) {
    throw new Error('Valid analyzer config is required');
  }

  const url = `${API_BASE_URL}/contract-analyzer/analyze`;
  try {
    const token = await getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ text: text.slice(0, 200_000), config }) // safety limit
    });

    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.message || text; } catch {}
      throw new Error(`LLM analysis failed: ${response.status} ${response.statusText}. ${message}`);
    }

    const data = await response.json();
    if (!data || !Array.isArray(data.findings)) {
      throw new Error('Unexpected response shape from LLM analysis');
    }
    const tokens = Number(data?.meta?.tokens_used || 0);
    const result: AnalyzerResult = {
      findings: data.findings as Finding[],
      meta: {
        tokenUsage: {
          input: tokens,
          output: 0,
          costEstimateUsd: undefined
        }
      }
    };
    return result;
  } catch (err) {
    console.error('Error during LLM analysis:', err);
    throw err instanceof Error ? err : new Error('Unknown error during LLM analysis');
  }
}

// Chunked analysis for long contracts. Splits text and calls backend multiple times.
export async function analyzeWithLLMChunked(text: string, config: AnalyzerConfig): Promise<AnalyzerResult> {
  if (!text || !text.trim()) return { findings: [] };
  // For short texts, use single call
  if (text.length <= 40000) {
    return analyzeWithLLM(text, config);
  }
  const maxChunk = 38000; // under 40k to keep prompt headroom
  const chunks: string[] = [];
  for (let i = 0; i < text.length; i += maxChunk) {
    chunks.push(text.slice(i, Math.min(text.length, i + maxChunk)));
    if (chunks.length >= 5) break; // cap to avoid excessive cost
  }

  const all: Finding[] = [];
  let tokenSum = 0;
  for (let idx = 0; idx < chunks.length; idx++) {
    try {
      const part = chunks[idx];
      const res = await analyzeWithLLM(part, config);
      all.push(...res.findings);
      tokenSum += Number(res.meta?.tokenUsage?.input || 0);
    } catch (e) {
      // continue other chunks
      // eslint-disable-next-line no-console
      console.warn('LLM chunk analysis failed for chunk', idx, e);
    }
  }

  // De-duplicate by title + category + jurisdiction; keep highest confidence
  const map = new Map<string, Finding>();
  for (const f of all) {
    const key = `${f.category}|${f.title}|${f.jurisdiction}`.toLowerCase();
    const prev = map.get(key);
    if (!prev || (f.confidence?.score || 0) > (prev.confidence?.score || 0)) {
      map.set(key, f);
    }
  }
  // Limit results for UI sanity
  const deduped = Array.from(map.values()).slice(0, 80);
  const result: AnalyzerResult = {
    findings: deduped,
    meta: { tokenUsage: { input: tokenSum, output: 0, costEstimateUsd: undefined } }
  };
  return result;
}
